<!--
  CreateEntityModal — Phase 4-7 / T34.

  Operator-facing modal that creates a host-side entity (lead, customer, etc.)
  from the current conversation. Renders a form derived from the host adapter's
  `formSchema`, pre-fills values from the matched customer summary plus the
  last few thread messages, posts via `createHostEntityMutation`, and on
  success calls back into ThreadPage which dispatches `openHostEntity` to the
  parent CRM, shows a toast, and closes.

  Visual direction is locked in the plan (T34 design — Plan 4 Phase 4-7).
  Key behaviors:
    - Centered horizontally, anchored 8% from top. min(560px, 95vw) wide.
    - Backdrop click-to-dismiss, Esc closes. NO confirm guard, NO focus trap.
    - Prefilled fields show a tinted background + `auto-filled` pill that both
      fade out on first user input.
    - Required = single accent-color "*" after label (no "required" word).
    - Validation blocks submit, focuses first invalid field, message = "required"
      (or one-line format hint for email/phone).
    - Submit states: idle | submitting ("Creating…") | success-flash | error
      (footer inline bar, no toast).
    - Open: backdrop fade 140ms + panel translateY(4px→0) opacity 200ms.
      Close reverses 140ms.
-->
<script lang="ts" module>
  // ---- exported types (consumed by ThreadPage etc.) -----------------------
  export type FormField =
    | {
        name: string;
        label: string;
        type: 'text' | 'email' | 'phone' | 'textarea';
        required: boolean;
        options?: never;
      }
    | {
        name: string;
        label: string;
        type: 'select';
        required: boolean;
        options: ReadonlyArray<{ value: string; label: string }>;
      };
</script>

<script lang="ts">
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { createHostEntityMutation } from '../lib/api/mutations.js';

  type Props = {
    entityType: string;
    displayName: string;
    formSchema: FormField[];
    conversationId: string;
    prefilledValues: Record<string, unknown>;
    prefilledContextNote: string;
    onClose: () => void;
    onSuccess: (entity: { entityType: string; entityId: string; deepLink: string }) => void;
  };

  let {
    entityType,
    displayName,
    formSchema,
    conversationId,
    prefilledValues,
    prefilledContextNote,
    onClose,
    onSuccess,
  }: Props = $props();

  // ---- form state ----------------------------------------------------------
  // Snapshot mount-time prop values into locals so $state init reads them
  // without triggering svelte's "captures initial value" warning. The modal
  // is re-mounted per Create ▾ selection, so capture-at-mount is correct.
  const _initSchema: FormField[] = formSchema;
  const _initPrefill: Record<string, unknown> = prefilledValues;
  const _initContext: string = prefilledContextNote ?? '';

  /** Live values keyed by field.name. Initialized from prefilledValues. */
  let values: Record<string, string> = $state(
    Object.fromEntries(
      _initSchema.map((f) => [f.name, coerceString(_initPrefill[f.name])]),
    ),
  );

  let contextNoteValue: string = $state(_initContext);

  /** Which fields started life with a non-empty prefill (drives tinted bg + pill). */
  let initiallyPrefilled: Record<string, boolean> = $state(
    Object.fromEntries(
      _initSchema.map((f) => [f.name, coerceString(_initPrefill[f.name]).length > 0]),
    ),
  );
  let contextInitiallyPrefilled = $state(_initContext.length > 0);

  /** Per-field error messages keyed by name. Empty string = no error. */
  let errors: Record<string, string> = $state(
    Object.fromEntries(_initSchema.map((f) => [f.name, ''])),
  );

  /** Whether the operator has tried submitting once — controls disabled-look. */
  let attemptedSubmit = $state(false);

  /** Server-side error bar text (footer left of buttons). Empty = no banner. */
  let serverError = $state('');

  /** Brief "success flash" between API success and modal close. */
  let successFlash = $state(false);

  /** Mount-side animation flag — flips true after first $effect tick. */
  let mounted = $state(false);

  // ---- TanStack mutation ---------------------------------------------------

  const queryClient = useQueryClient();
  const mutation = $derived(
    createMutation(createHostEntityMutation(queryClient, conversationId)),
  );

  const isSubmitting = $derived($mutation.isPending);

  // ---- refs ----------------------------------------------------------------

  let panelEl: HTMLDivElement | null = $state(null);
  let backdropEl: HTMLDivElement | null = $state(null);
  let submitBtnEl: HTMLButtonElement | null = $state(null);
  const fieldEls: Record<string, HTMLElement | null> = $state({});
  let contextEl: HTMLTextAreaElement | null = $state(null);

  // ---- focus on mount ------------------------------------------------------

  $effect(() => {
    // Flip the mounted flag on the next microtask so the open animation runs.
    queueMicrotask(() => {
      mounted = true;
    });

    // Focus: first non-prefilled required → context textarea → submit.
    const firstUnfilled = formSchema.find(
      (f) => f.required && !initiallyPrefilled[f.name],
    );
    if (firstUnfilled) {
      fieldEls[firstUnfilled.name]?.focus();
      return;
    }
    if (contextEl) {
      contextEl.focus();
      return;
    }
    submitBtnEl?.focus();
  });

  // ---- Esc to close (window keydown) ---------------------------------------

  $effect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  });

  // ---- helpers -------------------------------------------------------------

  function coerceString(input: unknown): string {
    if (input === undefined || input === null) return '';
    if (typeof input === 'string') return input;
    if (typeof input === 'number' || typeof input === 'boolean') return String(input);
    return '';
  }

  /** FNV-1a → 0..359 — deterministic hue per entity-type label. */
  function hashHue(input: string): number {
    let h = 2166136261;
    for (let i = 0; i < input.length; i++) {
      h ^= input.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return Math.abs(h) % 360;
  }

  const headerHue = $derived(hashHue(entityType));

  function inputMode(type: string): 'email' | 'tel' | 'text' {
    if (type === 'email') return 'email';
    if (type === 'phone') return 'tel';
    return 'text';
  }
  function autocompleteFor(
    type: string,
    name: string,
  ): 'email' | 'tel' | 'name' | 'on' {
    if (type === 'email') return 'email';
    if (type === 'phone') return 'tel';
    const n = name.toLowerCase();
    if (n.includes('name')) return 'name';
    return 'on';
  }

  // ---- input handlers ------------------------------------------------------

  function handleInput(name: string, next: string): void {
    values[name] = next;
    if (initiallyPrefilled[name]) {
      // First user input clears the "auto-filled" affordance.
      initiallyPrefilled[name] = false;
    }
    if (attemptedSubmit && errors[name]) {
      errors[name] = computeFieldError(getField(name), next);
    }
  }

  function handleContextInput(next: string): void {
    contextNoteValue = next;
    if (contextInitiallyPrefilled) contextInitiallyPrefilled = false;
  }

  function getField(name: string): FormField | undefined {
    return formSchema.find((f) => f.name === name);
  }

  function computeFieldError(field: FormField | undefined, raw: string): string {
    if (!field) return '';
    const v = raw.trim();
    if (field.required && v.length === 0) return 'required';
    if (v.length === 0) return ''; // optional + empty = ok
    if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      return 'Enter a valid email';
    }
    if (field.type === 'phone' && !/^[+()\d\s\-.]{5,}$/.test(v)) {
      return 'Enter a valid phone';
    }
    return '';
  }

  // ---- submit --------------------------------------------------------------

  function handleSubmit(event: SubmitEvent): void {
    event.preventDefault();
    attemptedSubmit = true;
    serverError = '';

    // Validate every field; first invalid gets focus.
    let firstInvalid: string | null = null;
    for (const field of formSchema) {
      const err = computeFieldError(field, values[field.name] ?? '');
      errors[field.name] = err;
      if (err && firstInvalid === null) firstInvalid = field.name;
    }
    if (firstInvalid) {
      fieldEls[firstInvalid]?.focus();
      return;
    }
    if (isSubmitting) return;

    const suggested: Record<string, string> = {};
    for (const f of formSchema) {
      const v = (values[f.name] ?? '').trim();
      if (v.length > 0) suggested[f.name] = v;
    }

    $mutation.mutate(
      // The mutation factory's CreateHostEntityInput typing keeps `prefill` —
      // we map our form payload onto it; the server treats unknown keys per
      // entityType. The factory's response is { entityType, entityId } but
      // the host adapter returns a deepLink too — we cast via `unknown` to
      // surface it without widening the shared mutation type today.
      { entityType, prefill: suggested, ...(contextNoteValue.trim() ? { contextNote: contextNoteValue.trim() } : {}) } as {
        entityType: string;
        prefill: Record<string, string>;
        contextNote?: string;
      },
      {
        onSuccess: (data) => {
          const enriched = data as unknown as {
            entityType: string;
            entityId: string;
            deepLink?: string | null;
          };
          successFlash = true;
          // Hold the flash for ~160ms then notify ThreadPage; total time to
          // close from click ≈ 260ms.
          setTimeout(() => {
            successFlash = false;
            onSuccess({
              entityType: enriched.entityType,
              entityId: enriched.entityId,
              deepLink: enriched.deepLink ?? '',
            });
          }, 160);
        },
        onError: (err) => {
          serverError = "Couldn't create " + entityType + '. Try again.';
          // Surface to console for triage; do NOT toast.
          // eslint-disable-next-line no-console
          console.warn('[widget] create-entity failed', err);
        },
      },
    );
  }

  function handleBackdropClick(event: MouseEvent): void {
    if (event.target === backdropEl && !isSubmitting) onClose();
  }

  function handleCancel(): void {
    if (!isSubmitting) onClose();
  }

  // ---- derived UI flags ----------------------------------------------------

  const hasAnyError = $derived(
    attemptedSubmit && formSchema.some((f) => errors[f.name].length > 0),
  );
  const submitDisabledLook = $derived(attemptedSubmit && hasAnyError);
</script>

<div
  bind:this={backdropEl}
  class="backdrop"
  class:mounted
  onclick={handleBackdropClick}
  onkeydown={() => { /* keyboard handled at window level */ }}
  role="presentation"
>
  <div
    bind:this={panelEl}
    class="panel"
    class:mounted
    role="dialog"
    aria-modal="true"
    aria-labelledby="create-entity-title"
  >
    <header class="hdr">
      <div class="hdr-row1">
        <span class="type-pill" style="--type-fg: oklch(48% 0.16 {headerHue})">{entityType}</span>
        <button
          type="button"
          class="close"
          aria-label="Close"
          onclick={handleCancel}
          disabled={isSubmitting}
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <h2 id="create-entity-title" class="title">
        New {displayName} from this conversation
      </h2>
    </header>

    <form class="body" onsubmit={handleSubmit} novalidate>
      <div class="fields">
        {#each formSchema as field (field.name)}
          {@const isPrefilled = initiallyPrefilled[field.name]}
          {@const err = errors[field.name]}
          <label class="field" class:prefilled={isPrefilled} class:has-error={!!err}>
            <span class="label-row">
              <span class="label-text">{field.label}</span>
              {#if field.required}<span class="req" aria-hidden="true">*</span>{/if}
              {#if isPrefilled}<span class="pill">auto-filled</span>{/if}
            </span>

            {#if field.type === 'textarea'}
              <textarea
                bind:this={fieldEls[field.name]}
                class="ctrl ctrl-area"
                name={field.name}
                rows="3"
                aria-required={field.required}
                aria-invalid={!!err}
                value={values[field.name]}
                oninput={(e) => handleInput(field.name, e.currentTarget.value)}
              ></textarea>
            {:else if field.type === 'select'}
              <div class="select-wrap">
                <select
                  bind:this={fieldEls[field.name]}
                  class="ctrl ctrl-select"
                  name={field.name}
                  aria-required={field.required}
                  aria-invalid={!!err}
                  value={values[field.name]}
                  onchange={(e) => handleInput(field.name, e.currentTarget.value)}
                >
                  <option value="" disabled selected={!values[field.name]}>Select…</option>
                  {#each field.options as opt (opt.value)}
                    <option value={opt.value}>{opt.label}</option>
                  {/each}
                </select>
                <span class="chev" aria-hidden="true">▾</span>
              </div>
            {:else}
              <input
                bind:this={fieldEls[field.name]}
                class="ctrl"
                type={field.type === 'phone' ? 'tel' : field.type}
                inputmode={inputMode(field.type)}
                autocomplete={autocompleteFor(field.type, field.name)}
                name={field.name}
                aria-required={field.required}
                aria-invalid={!!err}
                value={values[field.name]}
                oninput={(e) => handleInput(field.name, e.currentTarget.value)}
              />
            {/if}

            {#if err}
              <span class="err">{err}</span>
            {/if}
          </label>
        {/each}

        <!-- contextNote — rendered LAST regardless of formSchema order. -->
        <label class="field field-context" class:prefilled={contextInitiallyPrefilled}>
          <span class="label-row">
            <span class="label-text">Context note</span>
            {#if contextInitiallyPrefilled}<span class="pill">auto-filled</span>{/if}
          </span>
          <textarea
            bind:this={contextEl}
            class="ctrl ctrl-area"
            name="contextNote"
            rows="3"
            value={contextNoteValue}
            oninput={(e) => handleContextInput(e.currentTarget.value)}
          ></textarea>
        </label>
      </div>

      <footer class="ftr">
        {#if serverError}
          <div class="server-err" role="alert">{serverError}</div>
        {/if}
        <div class="spacer"></div>
        <button
          type="button"
          class="btn btn-ghost"
          onclick={handleCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          bind:this={submitBtnEl}
          type="submit"
          class="btn btn-primary"
          class:flash={successFlash}
          class:dim={submitDisabledLook}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Creating…' : `Create ${entityType}`}
        </button>
      </footer>
    </form>
  </div>
</div>

<style>
  /* ---- Backdrop + Panel motion ----------------------------------------- */

  .backdrop {
    position: fixed;
    inset: 0;
    background: oklch(20% 0.01 250 / 0.42);
    display: grid;
    grid-template-rows: 8% auto;
    justify-items: center;
    align-items: start;
    padding: 0 16px;
    z-index: 1000;
    opacity: 0;
    transition: opacity 140ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .backdrop.mounted {
    opacity: 1;
  }

  .panel {
    grid-row: 2;
    width: min(560px, 95vw);
    max-height: min(85vh, 720px);
    background: var(--widget-bg, oklch(99% 0.003 250));
    color: var(--widget-fg, oklch(20% 0.01 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: var(--widget-radius, 14px);
    box-shadow:
      0 2px 4px oklch(20% 0.01 250 / 0.08),
      0 24px 56px oklch(20% 0.01 250 / 0.18);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    opacity: 0;
    transform: translateY(4px);
    transition:
      opacity 200ms cubic-bezier(0.22, 1, 0.36, 1),
      transform 200ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .panel.mounted {
    opacity: 1;
    transform: translateY(0);
  }

  /* ---- Header ---------------------------------------------------------- */

  .hdr {
    padding: 18px 20px 14px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 0 auto;
  }
  .hdr-row1 {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 32px;
  }
  .type-pill {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--type-fg, oklch(48% 0.16 250));
  }
  .close {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: none;
    border: none;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }
  .close:hover:not(:disabled) {
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 6%, transparent);
    color: var(--widget-fg, oklch(20% 0.01 250));
  }
  .close:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(58% 0.18 260));
    outline-offset: 1px;
  }
  .close:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .title {
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--widget-fg, oklch(20% 0.01 250));
    line-height: 1.35;
  }

  /* ---- Body / form ----------------------------------------------------- */

  .body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
  }
  .fields {
    padding: 6px 20px 14px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 14px;
    flex: 1 1 auto;
    min-height: 0;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .label-row {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 14px;
  }
  .label-text {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .req {
    color: var(--widget-accent, oklch(58% 0.18 260));
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
  }
  .pill {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.09em;
    text-transform: uppercase;
    color: var(--widget-accent, oklch(58% 0.18 260));
    background: none;
    transition: opacity 120ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .ctrl {
    height: 36px;
    padding: 0 10px;
    font-size: 13px;
    color: var(--widget-fg, oklch(20% 0.01 250));
    background: var(--widget-bg, oklch(99% 0.003 250));
    border: 1px solid var(--widget-border, oklch(92% 0.005 250));
    border-radius: 8px;
    font-family: inherit;
    transition:
      background 120ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 120ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 120ms cubic-bezier(0.22, 1, 0.36, 1);
    width: 100%;
    box-sizing: border-box;
  }
  .ctrl-area {
    height: auto;
    min-height: 76px;
    max-height: 200px;
    padding: 8px 10px;
    line-height: 1.45;
    resize: vertical;
    field-sizing: content;
  }
  .ctrl-select {
    appearance: none;
    -webkit-appearance: none;
    padding-right: 28px;
  }
  .select-wrap {
    position: relative;
  }
  .select-wrap .chev {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
    pointer-events: none;
  }
  .ctrl:focus {
    outline: none;
    border-color: var(--widget-accent, oklch(58% 0.18 260));
    box-shadow: 0 0 0 2px color-mix(in oklch, var(--widget-accent, oklch(58% 0.18 260)) 35%, transparent);
  }

  .field.prefilled .ctrl {
    background: color-mix(in oklch, var(--widget-accent, oklch(58% 0.18 260)) 5%, var(--widget-bg, oklch(99% 0.003 250)));
    transition:
      background 120ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 120ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 120ms cubic-bezier(0.22, 1, 0.36, 1);
  }

  .field.has-error .ctrl {
    border-color: oklch(52% 0.16 25);
  }
  .field.has-error .ctrl:focus {
    box-shadow: 0 0 0 2px color-mix(in oklch, oklch(52% 0.16 25) 30%, transparent);
  }

  .err {
    font-size: 11.5px;
    color: oklch(52% 0.16 25);
    margin-top: 2px;
  }

  /* ---- Footer ---------------------------------------------------------- */

  .ftr {
    height: 60px;
    flex: 0 0 auto;
    border-top: 1px solid var(--widget-border, oklch(92% 0.005 250));
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 10px;
    background: var(--widget-bg, oklch(99% 0.003 250));
  }
  .server-err {
    background: color-mix(in oklch, oklch(52% 0.16 25) 8%, var(--widget-bg, oklch(99% 0.003 250)));
    color: oklch(40% 0.18 25);
    font-size: 12px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 6px;
    border: 1px solid color-mix(in oklch, oklch(52% 0.16 25) 18%, transparent);
  }
  .spacer {
    flex: 1 1 auto;
  }
  .btn {
    height: 36px;
    padding: 0 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    border: 1px solid transparent;
    font-family: inherit;
    transition:
      background 120ms cubic-bezier(0.22, 1, 0.36, 1),
      color 120ms cubic-bezier(0.22, 1, 0.36, 1),
      opacity 120ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .btn:focus-visible {
    outline: 2px solid var(--widget-accent, oklch(58% 0.18 260));
    outline-offset: 1px;
  }
  .btn-ghost {
    background: none;
    color: var(--widget-fg-muted, oklch(52% 0.012 250));
  }
  .btn-ghost:hover:not(:disabled) {
    color: var(--widget-fg, oklch(20% 0.01 250));
    background: color-mix(in oklch, var(--widget-fg, oklch(20% 0.01 250)) 5%, transparent);
  }
  .btn-primary {
    background: var(--widget-accent, oklch(58% 0.18 260));
    color: oklch(99% 0.003 250);
  }
  .btn-primary:hover:not(:disabled) {
    background: color-mix(in oklch, var(--widget-accent, oklch(58% 0.18 260)) 96%, black);
  }
  .btn-primary:disabled {
    opacity: 0.7;
    cursor: default;
  }
  .btn-primary.dim {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .btn-primary.flash {
    background: color-mix(in oklch, var(--widget-accent, oklch(58% 0.18 260)) 70%, oklch(68% 0.22 145));
    transition: background 160ms cubic-bezier(0.22, 1, 0.36, 1);
  }
</style>
